// src/contexts/AuthContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

// Pure native fetch - No dependencies
const api = {
  get: async (endpoint) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`https://new.sbdemolition.online/api${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` })
      }
    });
    
    if (response.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
      throw new Error('Unauthorized');
    }
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || `HTTP ${response.status}`);
    }
    return response.json();
  },
  
  post: async (endpoint, data) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`https://new.sbdemolition.online/api${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` })
      },
      body: JSON.stringify(data)
    });
    
    if (response.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
      throw new Error('Unauthorized');
    }
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || `HTTP ${response.status}`);
    }
    return response.json();
  },

  put: async (endpoint, data) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`https://new.sbdemolition.online/api${endpoint}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` })
      },
      body: JSON.stringify(data)
    });
    
    if (response.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  },

  del: async (endpoint) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`https://new.sbdemolition.online/api${endpoint}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` })
      }
    });
    
    if (response.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // ✅ Load user on mount
  useEffect(() => {
    loadUser();
  }, []);

  // ✅ Load user from localStorage
  const loadUser = () => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    console.log('🔍 Loading user from localStorage...');
    
    if (token && savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        console.log('✅ User loaded:', parsedUser);
        setUser(parsedUser);
      } catch (e) {
        console.error('❌ Failed to parse user:', e);
        localStorage.clear();
      }
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    try {
      console.log('🔐 Attempting login...');
      
      const response = await api.post('/auth/login.php', { email, password });
      
      console.log('📥 Login response:', response);
      
      if (response.success && response.token) {
        localStorage.setItem('token', response.token);
        localStorage.setItem('user', JSON.stringify(response.user));
        setUser(response.user);
        
        console.log('✅ Login successful:', response.user);
        
        return response.user;
      }
      throw new Error(response.message || 'Login failed');
    } catch (error) {
      console.error('LOGIN ERROR:', error);
      throw new Error(error.message || 'Invalid credentials');
    }
  };

  const logout = async () => {
    try { 
      await api.post('/auth/logout.php').catch(() => {}); 
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.clear();
      setUser(null);
      window.location.href = '/login';
    }
  };

  // ✅ Update user - sync with localStorage
  const updateUser = (updates) => {
    console.log('📝 Updating user with:', updates);
    
    const updatedUser = { ...user, ...updates };
    
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
    
    console.log('✅ User updated:', updatedUser);
  };

  const hasRole = (roles) => {
    if (!user) return false;
    if (Array.isArray(roles)) return roles.includes(user.role);
    return user.role === roles;
  };

  const value = {
    user,
    login,
    logout,
    updateUser, // ✅ Export updateUser
    hasRole,
    isAdmin: user?.role === 'Admin',
    isManager: ['Admin', 'Manager'].includes(user?.role),
    loading,
    api
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
